import { Injectable, inject, signal, computed, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, tap, catchError, throwError } from 'rxjs';
import { AffiliateDashboardStats } from '../models/affiliate.model';

@Injectable({
  providedIn: 'root',
})
export class AffiliateService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly platformId = inject(PLATFORM_ID);

  private readonly TOKEN_KEY = 'rhc_affiliate_token';

  token = signal<string | null>(this.getStoredToken());
  dashboardStats = signal<AffiliateDashboardStats | null>(null);
  isLoading = signal<boolean>(false);
  error = signal<string | null>(null);

  isAuthenticated = computed(() => !!this.token());

  constructor() {
    if (this.token() && isPlatformBrowser(this.platformId)) {
      this.fetchDashboard().subscribe();
    }
  }

  private getStoredToken(): string | null {
    if (isPlatformBrowser(this.platformId)) {
      return localStorage.getItem(this.TOKEN_KEY);
    }
    return null;
  }

  private setToken(newToken: string | null): void {
    this.token.set(newToken);
    if (isPlatformBrowser(this.platformId)) {
      if (newToken) {
        localStorage.setItem(this.TOKEN_KEY, newToken);
      } else {
        localStorage.removeItem(this.TOKEN_KEY);
      }
    }
  }

  private getAuthHeaders(): HttpHeaders {
    const t = this.token();
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      'Expires': '0',
      Authorization: t ? `Bearer ${t}` : '',
    });
  }

  register(data: { name: string; email: string; password: string; customSlug?: string }): Observable<any> {
    this.isLoading.set(true);
    this.error.set(null);

    return this.http.post<{ success: boolean; token: string; stats: AffiliateDashboardStats }>('/api/affiliates/register', data).pipe(
      tap((res) => {
        this.isLoading.set(false);
        if (res.token) {
          this.setToken(res.token);
          this.dashboardStats.set(res.stats);
        }
      }),
      catchError((err) => {
        this.isLoading.set(false);
        const msg = err?.error?.error || 'Registration failed. Please check your details.';
        this.error.set(msg);
        return throwError(() => new Error(msg));
      })
    );
  }

  login(data: { email: string; password: string }): Observable<any> {
    this.isLoading.set(true);
    this.error.set(null);

    return this.http.post<{ success: boolean; token: string; stats: AffiliateDashboardStats }>('/api/affiliates/login', data).pipe(
      tap((res) => {
        this.isLoading.set(false);
        if (res.token) {
          this.setToken(res.token);
          this.dashboardStats.set(res.stats);
        }
      }),
      catchError((err) => {
        this.isLoading.set(false);
        const msg = err?.error?.error || 'Invalid email or password.';
        this.error.set(msg);
        return throwError(() => new Error(msg));
      })
    );
  }

  fetchDashboard(): Observable<AffiliateDashboardStats> {
    this.isLoading.set(true);
    return this.http.get<AffiliateDashboardStats>('/api/affiliates/dashboard', { headers: this.getAuthHeaders() }).pipe(
      tap((stats) => {
        this.isLoading.set(false);
        this.dashboardStats.set(stats);
      }),
      catchError((err) => {
        this.isLoading.set(false);
        if (err.status === 401 || err.status === 403) {
          this.logout();
        }
        return throwError(() => err);
      })
    );
  }

  onboardStripeConnect(): Observable<{ url: string; isMock?: boolean; message?: string }> {
    this.isLoading.set(true);
    return this.http.post<{ url: string; isMock?: boolean; message?: string }>(
      '/api/affiliates/connect/onboard',
      {},
      { headers: this.getAuthHeaders() }
    ).pipe(
      tap((res) => {
        this.isLoading.set(false);
        if (res.isMock) {
          this.fetchDashboard().subscribe();
        }
      }),
      catchError((err) => {
        this.isLoading.set(false);
        return throwError(() => err);
      })
    );
  }

  getStripeConnectPortalLink(): Observable<{ url: string; isMock?: boolean; message?: string }> {
    this.isLoading.set(true);
    return this.http.post<{ url: string; isMock?: boolean; message?: string }>(
      '/api/affiliates/connect/portal',
      {},
      { headers: this.getAuthHeaders() }
    ).pipe(
      tap(() => this.isLoading.set(false)),
      catchError((err) => {
        this.isLoading.set(false);
        return throwError(() => err);
      })
    );
  }

  updatePayoutSettings(payoutMethod: 'stripe' | 'paypal', paypalEmail?: string): Observable<any> {
    this.isLoading.set(true);
    this.error.set(null);
    return this.http.post<{ success: boolean; message: string; stats: AffiliateDashboardStats }>(
      '/api/affiliates/payout-settings',
      { payoutMethod, paypalEmail },
      { headers: this.getAuthHeaders() }
    ).pipe(
      tap((res) => {
        this.isLoading.set(false);
        if (res.stats) {
          this.dashboardStats.set(res.stats);
        }
      }),
      catchError((err) => {
        this.isLoading.set(false);
        const msg = err?.error?.error || 'Failed to update payout settings.';
        this.error.set(msg);
        return throwError(() => new Error(msg));
      })
    );
  }

  logout(): void {

    this.setToken(null);
    this.dashboardStats.set(null);
    this.router.navigate(['/affiliates/login']);
  }
}
